/**
 * Grill King header — gliding nav indicator + dropdown item stagger.
 *
 * One orange bar slides between top-level items on hover/focus and rests on
 * the active item (or fades out when there is none). It replaces the per-link
 * underline only once this script has run (.gk-nav-glide on the list), so
 * without JS the original CSS underline still works.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function linkOf(item) {
    return item.querySelector(':scope > .fp-desktop-nav__link, :scope > header-menu .fp-desktop-nav__link');
  }

  function initGlide(list) {
    if (list.dataset.gkGlide === '1') return;
    list.dataset.gkGlide = '1';

    var bar = document.createElement('span');
    bar.className = 'gk-nav-glide__bar';
    bar.setAttribute('aria-hidden', 'true');
    list.appendChild(bar);
    list.classList.add('gk-nav-glide');

    var items = Array.prototype.filter.call(list.children, function (el) {
      return el.tagName === 'LI';
    });

    function activeItem() {
      for (var i = 0; i < items.length; i++) {
        var link = linkOf(items[i]);
        if (link && link.classList.contains('is-active')) return items[i];
      }
      return null;
    }

    var PAD_X = 12;
    var PILL_H = 34;
    var hovering = false;

    function moveTo(item, instant) {
      var link = item && linkOf(item);
      if (!link) {
        bar.style.opacity = '0';
        return;
      }
      var listRect = list.getBoundingClientRect();
      var r = link.getBoundingClientRect();
      var idx = items.indexOf(item);
      var prev = idx > 0 && linkOf(items[idx - 1]);
      var next = idx < items.length - 1 && linkOf(items[idx + 1]);
      /* Stay inside half of each neighbouring gap so the pill never touches a label. */
      var padL = prev ? Math.min(PAD_X, Math.max(4, (r.left - prev.getBoundingClientRect().right) / 2 - 1)) : PAD_X;
      var padR = next ? Math.min(PAD_X, Math.max(4, (next.getBoundingClientRect().left - r.right) / 2 - 1)) : PAD_X;
      if (instant || reduce.matches) bar.classList.add('is-instant');
      bar.classList.toggle('is-active', link.classList.contains('is-active') && !hovering);
      bar.style.top = Math.round(r.top - listRect.top + r.height / 2 - PILL_H / 2) + 'px';
      bar.style.height = PILL_H + 'px';
      bar.style.width = r.width + padL + padR + 'px';
      bar.style.transform = 'translateX(' + (r.left - listRect.left - padL) + 'px)';
      bar.style.opacity = '1';
      if (instant || reduce.matches) {
        void bar.offsetWidth;
        bar.classList.remove('is-instant');
      }
    }

    items.forEach(function (item) {
      item.addEventListener('pointerenter', function () {
        var wasHovering = hovering;
        hovering = true;
        moveTo(item, !wasHovering && bar.style.opacity !== '1');
      });
      item.addEventListener('focusin', function () {
        moveTo(item, false);
      });
    });

    function rest() {
      hovering = false;
      if (list.querySelector('details[open]')) return;
      moveTo(activeItem(), false);
    }

    list.addEventListener('pointerleave', rest);
    list.addEventListener('focusout', function (e) {
      if (!list.contains(e.relatedTarget)) rest();
    });
    list.addEventListener('toggle', function () {
      if (!list.matches(':hover')) rest();
    }, true);

    var raf = 0;
    window.addEventListener('resize', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        moveTo(activeItem(), true);
      });
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        moveTo(activeItem(), true);
      });
    }
    moveTo(activeItem(), true);
  }

  function indexSubmenus(scope) {
    var menus = (scope || document).querySelectorAll('.fp-desktop-nav__submenu');
    Array.prototype.forEach.call(menus, function (menu) {
      var i = 0;
      Array.prototype.forEach.call(menu.children, function (li) {
        li.style.setProperty('--gk-i', String(Math.min(i++, 12)));
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Search panel extras                                                 */
  /* ------------------------------------------------------------------ */

  var RECENT_KEY = 'gk-recent-searches';
  var RECENT_MAX = 6;

  function readRecent() {
    try {
      var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      if (!Array.isArray(list)) return [];
      return list.filter(function (t) {
        return typeof t === 'string' && t.trim();
      }).slice(0, RECENT_MAX);
    } catch (e) {
      return [];
    }
  }

  function saveRecent(term) {
    term = (term || '').trim().slice(0, 60);
    if (term.length < 2) return;
    var list = readRecent().filter(function (t) {
      return t.toLowerCase() !== term.toLowerCase();
    });
    list.unshift(term);
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {}
  }

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Wraps the typed words inside result titles in <mark>, text nodes only. */
  function highlight(root, query) {
    var words = (query || '').trim().split(/\s+/).filter(function (w) {
      return w.length > 1;
    });
    if (!words.length) return;
    var re = new RegExp('(' + words.map(escapeRegExp).join('|') + ')', 'gi');
    Array.prototype.forEach.call(root.querySelectorAll('.predictive-search__item-heading:not([data-gk-hl])'), function (heading) {
      heading.setAttribute('data-gk-hl', '');
      var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (node) {
        var text = node.nodeValue;
        re.lastIndex = 0;
        if (!re.test(text)) return;
        var frag = document.createDocumentFragment();
        text.split(re).forEach(function (part, i) {
          if (!part) return;
          if (i % 2 === 1) {
            var mark = document.createElement('mark');
            mark.className = 'gk-hl';
            mark.textContent = part;
            frag.appendChild(mark);
          } else {
            frag.appendChild(document.createTextNode(part));
          }
        });
        node.parentNode.replaceChild(frag, node);
      });
    });
  }

  function initSearch(details) {
    if (details.dataset.gkSearch === '1') return;
    details.dataset.gkSearch = '1';

    var form = details.querySelector('form[role="search"]');
    var input = details.querySelector('input[type="search"]');
    if (!form || !input) return;
    var searchUrl = form.getAttribute('action') || '/search';
    var recentGroup = details.querySelector('[data-gk-recent]');
    var recentList = details.querySelector('[data-gk-recent-list]');

    function renderRecent() {
      if (!recentGroup || !recentList) return;
      var items = readRecent();
      recentList.textContent = '';
      items.forEach(function (term) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'gk-search-chip gk-search-chip--recent';
        a.href = searchUrl + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
        a.textContent = term;
        li.appendChild(a);
        recentList.appendChild(li);
      });
      recentGroup.hidden = items.length === 0;
    }

    var clearBtn = details.querySelector('[data-gk-recent-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        try {
          window.localStorage.removeItem(RECENT_KEY);
        } catch (e) {}
        renderRecent();
        input.focus();
      });
    }

    form.addEventListener('submit', function () {
      saveRecent(input.value);
    });
    details.addEventListener('click', function (e) {
      var chip = e.target.closest('.gk-search-chip');
      if (chip) {
        saveRecent(chip.textContent);
        return;
      }
      if (e.target.closest('.predictive-search__item')) saveRecent(input.value);
    });

    /* Typewriter placeholder: cycles real search terms while the box is empty. */
    var baseHolder = input.getAttribute('placeholder') || '';
    var terms = (input.getAttribute('data-gk-type-terms') || '').split(',').map(function (t) {
      return t.trim();
    }).filter(Boolean);
    var typeTimer = 0;
    var ti = 0;
    var ci = 0;
    var deleting = false;

    function stopType() {
      clearTimeout(typeTimer);
      typeTimer = 0;
      input.setAttribute('placeholder', baseHolder);
    }

    function typeTick() {
      if (input.value) {
        input.setAttribute('placeholder', baseHolder);
        typeTimer = setTimeout(typeTick, 1200);
        return;
      }
      var word = terms[ti];
      var delay = deleting ? 35 : 75;
      if (!deleting) {
        ci += 1;
        if (ci > word.length) {
          deleting = true;
          delay = 1500;
          ci = word.length;
        }
      } else {
        ci -= 1;
        if (ci < 0) {
          deleting = false;
          ci = 0;
          ti = (ti + 1) % terms.length;
          delay = 280;
        }
      }
      input.setAttribute('placeholder', 'Try \u201C' + word.slice(0, ci) + '\u201D');
      typeTimer = setTimeout(typeTick, delay);
    }

    function startType() {
      if (!terms.length || reduce.matches || typeTimer) return;
      ti = 0;
      ci = 0;
      deleting = false;
      typeTimer = setTimeout(typeTick, 900);
    }

    details.addEventListener('toggle', function () {
      if (details.open) {
        renderRecent();
        startType();
      } else {
        stopType();
      }
    });

    /* Highlight matches whenever predictive-search swaps in new results. */
    var resultsHost = details.querySelector('[data-predictive-search]');
    if (resultsHost && typeof MutationObserver !== 'undefined') {
      new MutationObserver(function () {
        highlight(resultsHost, input.value);
      }).observe(resultsHost, { childList: true, subtree: true });
    }

    /* Voice search where the browser supports it. */
    var voiceBtn = details.querySelector('[data-gk-search-voice]');
    var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (voiceBtn && SR) {
      voiceBtn.hidden = false;
      var rec = null;
      voiceBtn.addEventListener('click', function () {
        if (rec) {
          rec.stop();
          return;
        }
        try {
          rec = new SR();
        } catch (e) {
          voiceBtn.hidden = true;
          return;
        }
        rec.lang = document.documentElement.lang === 'en' ? 'en-AU' : document.documentElement.lang || 'en-AU';
        rec.interimResults = true;
        rec.maxAlternatives = 1;
        stopType();
        voiceBtn.classList.add('is-listening');
        voiceBtn.setAttribute('aria-label', 'Stop voice search');
        input.setAttribute('placeholder', 'Listening\u2026');
        rec.onresult = function (event) {
          var text = '';
          for (var i = 0; i < event.results.length; i++) text += event.results[i][0].transcript;
          input.value = text;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        };
        var done = function () {
          voiceBtn.classList.remove('is-listening');
          voiceBtn.setAttribute('aria-label', 'Search by voice');
          input.setAttribute('placeholder', baseHolder);
          input.focus();
          rec = null;
        };
        rec.onend = done;
        rec.onerror = done;
        rec.start();
      });
    }
  }

  /* "/" anywhere (outside fields) or Cmd/Ctrl+K opens search. */
  function bindShortcuts() {
    if (window.__gkSearchKeys) return;
    window.__gkSearchKeys = true;
    document.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.altKey) return;
      var t = e.target;
      var typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      var isK = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      var isSlash = e.key === '/' && !typing && !e.metaKey && !e.ctrlKey;
      if (!isK && !isSlash) return;
      var details = document.querySelector('details.fp-header-search');
      if (!details || !details.offsetParent && !details.getClientRects().length) return;
      e.preventDefault();
      if (isK && details.open) {
        details.removeAttribute('open');
        return;
      }
      details.setAttribute('open', '');
    });
  }

  function init(scope) {
    var host = scope || document;
    Array.prototype.forEach.call(host.querySelectorAll('.fp-desktop-nav--inline .fp-desktop-nav__list'), initGlide);
    indexSubmenus(host);
    Array.prototype.forEach.call(host.querySelectorAll('details.fp-header-search'), initSearch);
    bindShortcuts();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
